/**
 * Third-party integration account React Query key factory.
 */
function integrationStatusKeys(provider: string) {
  return {
    status: (businessId: string) => ['integrations', provider, 'status', businessId] as const,
    statusNone: () => ['integrations', provider, 'status', 'none'] as const,
  };
}

export const integrationsQueryKeys = {
  settings: {
    memberSelfConnect: (businessId: string | undefined) =>
      ['integrations', 'settings', 'memberSelfConnect', businessId] as const,
    connectorForSharing: (businessId: string | undefined) =>
      ['integrations', 'settings', 'connectorForSharing', businessId] as const,
  },
  permittedAll: (businessId: string | undefined) =>
    ['integrations', 'accounts', 'permittedAll', businessId] as const,
  accounts: {
    emailSend: (businessId: string | undefined) =>
      ['integrations', 'accounts', 'emailSend', businessId] as const,
    calendarUse: (businessId: string | undefined, scope?: 'meeting-details') =>
      scope
        ? (['integrations', 'accounts', 'calendarUse', businessId, scope] as const)
        : (['integrations', 'accounts', 'calendarUse', businessId] as const),
    meetingCreate: (businessId: string | undefined, scope?: 'meeting-details') =>
      scope
        ? (['integrations', 'accounts', 'meetingCreate', businessId, scope] as const)
        : (['integrations', 'accounts', 'meetingCreate', businessId] as const),
  },
  email: {
    defaultSender: (businessId: string | undefined) =>
      ['integrations', 'email', 'defaultSender', businessId] as const,
  },
  gmail: integrationStatusKeys('gmail'),
  outlook: integrationStatusKeys('outlook'),
  zoom: integrationStatusKeys('zoom'),
  meta: integrationStatusKeys('meta'),
  canva: {
    ...integrationStatusKeys('canva'),
    projectDesigns: (projectId: string) =>
      ['integrations', 'canva', 'projectDesigns', projectId] as const,
    userDesigns: () => ['integrations', 'canva', 'userDesigns'] as const,
  },
  connecteam: {
    ...integrationStatusKeys('connecteam'),
    jobs: (businessId: string) => ['integrations', 'connecteam', 'jobs', businessId] as const,
    jobLinks: (businessId: string) =>
      ['integrations', 'connecteam', 'jobLinks', businessId] as const,
    roster: (businessId: string) => ['integrations', 'connecteam', 'roster', businessId] as const,
    rosterNone: () => ['integrations', 'connecteam', 'roster', 'none'] as const,
  },
  snappic: integrationStatusKeys('snappic'),
  boothEvents: integrationStatusKeys('booth-events'),
  touchPix: integrationStatusKeys('touchpix'),
  simpleBooth: integrationStatusKeys('simple-booth'),
  quickbooks: {
    ...integrationStatusKeys('quickbooks'),
    accounts: (businessId: string) =>
      ['integrations', 'quickbooks', 'accounts', businessId] as const,
    paymentAccounts: (businessId: string) =>
      ['integrations', 'quickbooks', 'payment-accounts', businessId] as const,
    items: (businessId: string) => ['integrations', 'quickbooks', 'items', businessId] as const,
    taxCodes: (businessId: string) =>
      ['integrations', 'quickbooks', 'tax-codes', businessId] as const,
    syncJobs: (businessId: string) =>
      ['integrations', 'quickbooks', 'sync-jobs', businessId] as const,
    entitySync: (localType: string | null, localId: string | null) =>
      ['integrations', 'quickbooks', 'entity-sync', localType, localId] as const,
  },
  quo: {
    ...integrationStatusKeys('quo'),
    fromOptions: (businessId: string) =>
      ['integrations', 'quo', 'fromOptions', businessId] as const,
    fromOptionsNone: () => ['integrations', 'quo', 'fromOptions', 'none'] as const,
  },
  twilio: {
    ...integrationStatusKeys('twilio'),
    senders: (businessId: string) => ['integrations', 'twilio', 'senders', businessId] as const,
  },
  /** RCS document card templates (Preferences > Messages). Root key invalidates every business. */
  rcsCardTemplates: {
    root: () => ['texting', 'rcsCardTemplates'] as const,
    list: (businessId: string) => ['texting', 'rcsCardTemplates', businessId] as const,
    listNone: () => ['texting', 'rcsCardTemplates', 'none'] as const,
  },
  /** Active texting provider (Quo or Twilio) for the business. Root key invalidates every business. */
  texting: {
    root: () => ['texting', 'activeProvider'] as const,
    activeProvider: (businessId: string) => ['texting', 'activeProvider', businessId] as const,
    activeProviderNone: () => ['texting', 'activeProvider', 'none'] as const,
  },
  templatesBooth: integrationStatusKeys('templatesbooth'),
  prismm: integrationStatusKeys('prismm'),
  smugMug: {
    status: (businessId: string) => ['integrations', 'smugmug', 'status', businessId] as const,
    statusNone: () => ['integrations', 'smugmug', 'status', 'none'] as const,
  },
};
